import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DATE_FORMAT } from "@/constants/datetime";
import { MessageBox } from "@/features/chat/components/MessageBox";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { useDialog } from "@/hooks/useDialog";
import { useMount } from "@/hooks/useMount";
import { text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { Box, CircularProgress, css, styled, Typography } from "@mui/material";
import { Fragment, useEffect, useState } from "react";

export const Room = () => {
  const { currentRoom, roomDeletedMessage, isRoomDeleted } = useChat();
  const isMounted = useMount();
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const confirmModal = useDialog();

  const handleRemoveMessage = async (id: number) => {
    try {
      const agree = await confirmModal.show();
      if (!agree) return;
      await currentRoom?.deleteMsg(id);
    } catch (error) {
      showError(error);
    }
  };

  const handleScroll = async () => {
    if (!currentRoom.roomRef.current || isFetchingMore || !currentRoom.hasNextPage) return;

    const scrollContainer = currentRoom.roomRef.current;
    if (scrollContainer?.scrollTop === 0) {
      try {
        setIsFetchingMore(true);
        scrollContainer.scrollTop = 20;
        await currentRoom.fetchNextPage();
      } catch (error) {
        showError(error);
      } finally {
        setIsFetchingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!isMounted) return;
    const scrollContainer = currentRoom.roomRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [isMounted, currentRoom]);

  const isExistMessageList = [...currentRoom.oldMessages, ...currentRoom.newMessages].length > 0;

  // Scroll to bottom on initial mount
  useEffect(() => {
    if (!isMounted && !isExistMessageList) return;
    currentRoom.scrollToBottom();
  }, [isMounted, isExistMessageList]);

  return (
    <>
      <RoomWrapper isFetchingMore={currentRoom.isFetching || isFetchingMore} ref={currentRoom.roomRef}>
        {(currentRoom.isFetching || isFetchingMore) && (
          <SpinnerContainer>
            <CircularProgress color={"primary"} />
          </SpinnerContainer>
        )}
        {isRoomDeleted ? (
          <Typography component="p" my={2} textAlign="center" variant="sub16Semi" color={text.secondary}>
            {roomDeletedMessage}
          </Typography>
        ) : (
          [...currentRoom.oldMessages, ...currentRoom.newMessages].map((item, index, arr) => {
            const currentDate = item.createdAt ? dayjs(item.createdAt).format(DATE_FORMAT) : null;
            const prevDate = index > 0 && arr[index - 1].createdAt ? dayjs(arr[index - 1].createdAt).format(DATE_FORMAT) : null;

            return (
              <Fragment key={item.id}>
                {currentDate && currentDate !== prevDate && (
                  <Typography component="p" my={2} textAlign="center" variant="sub16Semi" color={text.secondary}>
                    {currentDate}
                  </Typography>
                )}
                <MessageBox key={item.id} {...item} handleRemoveMessage={handleRemoveMessage} />
              </Fragment>
            );
          })
        )}
      </RoomWrapper>

      <ConfirmSaveModal open={confirmModal.open} onOk={confirmModal.confirm} onClose={confirmModal.cancel}>
        <Typography>削除すると元に戻せません。削除してもよろしいですか？</Typography>
      </ConfirmSaveModal>
    </>
  );
};

const options = { shouldForwardProp: (propName: string) => !["isFetchingMore"].includes(propName) };

const RoomWrapper = styled(Box, options)<{ isFetchingMore: boolean }>`
  position: relative;
  width: 100%;
  max-height: calc(100% - 200px);
  padding: 8px 16px;
  border-radius: 8px;
  ${({ isFetchingMore }) =>
    isFetchingMore
      ? css`
          overflow: hidden;
        `
      : css`
          overflow: auto;
        `};
  background-color: white;
  flex: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SpinnerContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;
